import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { MailAttachement, Setting } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { DownloadFilesTask } from '../../issue/classes';
import { MsgPreviewDialogComponent } from '../../mail/components/dialog/msg-preview-dialog/msg-preview-dialog.component';
import { WaitComponent } from '../../shared/components/dialog/wait/wait.component';
import { SessionService } from '../../shared/services/session.service';
import { TokenService } from '../../shared/services/token.service';
import { NextcloudFile, NextcloudPathInfo } from '../classes';
import { OnlyofficeDialogComponent, OnlyofficeDialogComponentData } from '../components/dialog/onlyoffice-dialog/onlyoffice-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class FileService {

  static nextcloudPrefix = 'remote.php/dav/files';

  constructor(
    private _tokenService: TokenService,
    private _httpClient: HttpClient,
    private _sessionService: SessionService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar
  ) { }

  createNewFolder = (path: string) => this._httpClient.request("post", `${serverLocation}/api/nextcloud/folder/create`, {
    headers: new HttpHeaders().set("Authorization", "Bearer " + this._tokenService.token),
    body: { "path": path }
  });

  getFileByPath(path: string): Observable<Blob> {
    return this._sessionService.getSettingByName(SessionService.globalSettingNames.NEXTCLOUD_USERNAME).pipe(
      take(1),
      switchMap((setting: Setting) => this._httpClient.request("post", serverLocation + "/api/nextcloud/file", {
        headers: new HttpHeaders().set("Authorization", "Bearer " + this._tokenService.token),
        body: { "path": path, "username": setting.stgvalue },
        responseType: "blob"
      }))
    );
  }

  getFolderByPath(path: string): Observable<NextcloudFile[]> {
    let auth = new HttpHeaders().set("Authorization", "Bearer " + this._tokenService.token);
    return (this._sessionService.getSettingByName(SessionService.globalSettingNames.NEXTCLOUD_USERNAME).pipe(
      switchMap((setting: Setting) => setting ? this._httpClient.request("post", `${serverLocation}/api/nextcloud/folder`, {
        headers: auth,
        body: { "path": path, "username": setting.stgvalue }
      }) : of(null))
    ) as Observable<NextcloudFile[]>).pipe(tap((files) => {
      if (Array.isArray(files)) files.forEach(x => x.isNextcloudFile = true);
    }));
  }

  getRelativePath(path: string): Observable<NextcloudPathInfo> {
    let output = { pathIsEmpty: typeof path !== 'string', initialPath: path, initialPathIncludesNextcloudPrefix: false, nextcloudUsername: null, isFile: false, isFolder: false } as NextcloudPathInfo;
    if (!output.pathIsEmpty) {
      let subject = new Subject<NextcloudPathInfo>();
      this._sessionService.getSettingByName('nextcloud-username')
        .subscribe(
          (setting: Setting) => {
            if (!setting || !setting.stgvalue) {
              output.nextcloudUserNameMissing = true;
              return output;
            }
            output.rootFolderName = 'Startverzeichnis';
            output.nextcloudUsername = setting.stgvalue;
            output.initialPathIncludesNextcloudUsername = path.indexOf(setting.stgvalue) > -1;
            output.prettyPathArray = [];
            if (path.startsWith('/')) path = path.slice(1);
            if (path === '') {
              output.convertedPath = `${FileService.nextcloudPrefix}/${setting.stgvalue}/`;
              output.relativeConvertedPath = `/`;
              output.isFolder = true;
            } else {
              output.initialPathIncludesNextcloudPrefix = path.startsWith(FileService.nextcloudPrefix);
              let segments = path.split('/');
              if (output.initialPathIncludesNextcloudPrefix) segments = segments.slice(3);
              if (segments[0] === setting.stgvalue) segments = segments.slice(1);
              var relativePath = '';
              var index = 0;
              let currentSegment = segments[index];
              while (typeof currentSegment === 'string') {
                currentSegment = currentSegment.trim();
                if (currentSegment === '' && index === (segments.length - 1)) output.isFolder = true;
                else {
                  relativePath += `${currentSegment}/`;
                  output.rootFolderName = decodeURIComponent(currentSegment);
                  output.prettyPathArray.push(decodeURIComponent(currentSegment));
                }
                index++;
                currentSegment = segments[index];
              }
              output.relativeConvertedPath = relativePath;
              output.convertedPath = `${FileService.nextcloudPrefix}/${output.nextcloudUsername}/${relativePath}`;
            }
            output.isFile = !output.isFolder;
            subject.next(output);
          },
          (error) => {
            output.nextcloudUserNameMissing = true;
            subject.next(output);
          },
          () => subject.complete()
        );
    }
    return of(output);
  }

  filesToZip(task: DownloadFilesTask) {
    let auth = new HttpHeaders().set("Authorization", "Bearer " + this._tokenService.token);
    return this._httpClient.request("post", serverLocation + "/api/nextcloud/zip", {
      headers: auth,
      body: task,
      responseType: "blob"
    });
  }

  static getFileName(href: string): string {
    let array = href.trim().split('/');
    let lastSection = array[array.length - 1];
    return lastSection ? lastSection : array[array.length - 2];
  }

  static getFileType(type: string, extension: string = null): string {
    var result = null;
    switch (type) {
      case "image/png":
        result = "png";
        break;
      case "image/jpeg":
        result = "jpg";
        break;
      case "text/plain":
        result = "txt";
        break;
      case "text/html":
        result = "html";
        break;
      case "application/pdf":
        result = "pdf";
        break;
      case "application/json":
        result = "json";
        break;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        result = "xlsx";
        break;
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        result = "docx";
        break;
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        result = "pptx";
        break;
      case "application/vnd.ms-excel.sheet.macroenabled.12":
      case "application/vnd.ms-excel.sheet.macroEnabled.12":
        result = "xlsm";
        break;
      case "text/csv":
        result = "csv";
        break;
      case "folder":
        result = "folder";
        break;
      case "video/mp4":
        result = "mp4";
        break;
      case "application/x-zip-compressed":
        result = "zip";
        break;
      default:
        console.warn("unknown file type: " + type);
        break;
    }
    if (!result) {
      switch (extension) {
        case 'p7s':
          result = 'p7b';
          break;
        case 'sql':
          result = 'sql';
          break;
        case 'pdf':
          result = 'pdf';
          break;
        case 'msg':
          result = 'fax';
          break;
        default:
          result = 'blank';
          break;
      }
    }
    return result ? `fiv-icon-${result}` : "fiv-icon-blank";
  }

  static isFileTypeIntendedForOORendering(type: string): boolean {
    switch (type) {
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      case "application/vnd.ms-excel.sheet.macroenabled.12":
      case "application/vnd.ms-excel.sheet.macroEnabled.12":
        return true;
    }
    return false;
  }

  static isFileTypeIntendedForPreview(type: string): boolean {
    switch (type) {
      case "application/pdf":
      case "video/mp4":
      case "image/jpeg":
      case "image/png":
        return true;
    }
    return false;
  }

  previewBlob(blob: Blob, type: string, fileName: string) {
    if (FileService.isFileTypeIntendedForPreview(type)) window.open(URL.createObjectURL(blob));
    else {
      let array = fileName.split('.');
      if (array.length > 1 && array[array.length - 1] === 'msg') this._dialog.open(MsgPreviewDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          blob: blob,
          fileName: fileName
        }
      });
      else {
        var downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(type);
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }
  }

  previewFile(file: NextcloudFile) {
    if (FileService.isFileTypeIntendedForOORendering(file.type)) {
      this._dialog.open(OnlyofficeDialogComponent, {
        panelClass: "no-padding-dialog-container",
        autoFocus: false,
        data: {
          'file': file
        } as OnlyofficeDialogComponentData
      });
    } else {
      let ref = this._dialog.open(WaitComponent, { disableClose: true, data: { headline: 'Datei wird geladen' } });
      this.getFileByPath(file.href).subscribe({
        next: (result: Blob) => this.previewBlob(result, file.type, FileService.getFileName(file.href)),
        error: (error: HttpErrorResponse) => this._snackBar.open(`Problem beim Anzeigen der Datei: ${error.message}`),
        complete: () => ref.close()
      });
    }
  }

  uploadFile(path: string, data: FormData): Observable<any> {
    return this._httpClient.put(serverLocation + "/api/nextcloud/file", data, {
      headers: new HttpHeaders()
        .set("Authorization", "Bearer " + this._tokenService.token)
        .set("Path", path)
    });
  }

  static prettyFileSize(value: string): string {
    var size = parseFloat(value);
    if (size < 1000) return (size).toFixed(0) + " Byte";
    size = size / 1000;
    if (size < 1000) return (size).toFixed(2) + " kB";
    size = size / 1000;
    if (size < 1000) return (size).toFixed(2) + " MB";
    size = size / 1000;
    if (size < 1000) return (size).toFixed(2) + " GB";
    size = size / 1000;
    if (size < 1000) return (size).toFixed(2) + " TB";
    size = size / 1000;
    if (size < 1000) return (size).toFixed(2) + " PB";
    return size.toFixed(2);
  }

}